import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Session-free client: a stale login token in localStorage must never
// poison public reads (the careers/survey "empty page" bug class).
import { publicSupabase as supabase } from '../lib/supabase';
import { ErrorState } from '../components/ui';

// Amenity icons
const AMENITY_ICONS = {
  mushola: '🕌',
  wifi: '📶',
  valet: '🚗',
  lesehan: '🪑',
  live_music: '🎵',
  toilets: '🚻',
  parking: '🅿️',
  ac: '❄️',
  outdoor: '🌳',
};

// Service colors
const SERVICE_COLORS = {
  dine_in: 'bg-green-100 text-green-700',
  takeaway: 'bg-blue-100 text-blue-700',
  delivery: 'bg-purple-100 text-purple-700',
  catering: 'bg-orange-100 text-orange-700',
  reservation: 'bg-pink-100 text-pink-700',
};

export default function BranchDetail() {
  const { branchId } = useParams();
  const { t } = useTranslation();
  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchBranch();
  }, [branchId]);

  const fetchBranch = async () => {
    setLoading(true);
    setError(null);
    // Try to find by slug first, then by id
    let { data, error: fetchError } = await supabase
      .from('website_branches')
      .select('*')
      .eq('slug', branchId)
      .eq('is_active', true)
      .single();

    // If not found by slug, try by id
    if (fetchError || !data) {
      const result = await supabase
        .from('website_branches')
        .select('*')
        .eq('id', branchId)
        .eq('is_active', true)
        .single();

      data = result.data;
      fetchError = result.error;
    }

    if (data) {
      setBranch(data);
    } else if (fetchError && fetchError.code !== 'PGRST116') {
      // PGRST116 = no rows found (a genuine "not found", not an error)
      console.error('Error fetching branch:', fetchError);
      setError(fetchError);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin"></div>
          <span className="text-gray-500">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <ErrorState onRetry={fetchBranch} />
      </div>
    );
  }

  if (!branch) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
        <p className="text-gray-500 mb-4">Branch not found</p>
        <Link to="/branches" className="btn-primary">
          {t('common.back')}
        </Link>
      </div>
    );
  }

  // Collect all images for gallery
  const allImages = [
    ...(branch.featured_image_url ? [branch.featured_image_url] : []),
    ...(branch.gallery_urls || []),
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Image */}
      {branch.featured_image_url && (
        <div className="relative h-64 md:h-96 bg-gray-200">
          <img
            src={branch.featured_image_url}
            alt={branch.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            <div className="max-w-4xl mx-auto">
              <Link to="/branches" className="inline-flex items-center text-white/80 hover:text-white mb-3 text-sm">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                {t('branches.title')}
              </Link>
              <h1 className="text-3xl md:text-4xl font-display font-bold text-white">{branch.name}</h1>
              {branch.city && (
                <p className="text-white/80 mt-2">{branch.city}, {branch.region}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Link (if no hero image) */}
        {!branch.featured_image_url && (
          <Link to="/branches" className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-6">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('common.back')}
          </Link>
        )}

        {/* Title (if no hero image) */}
        {!branch.featured_image_url && (
          <h1 className="text-3xl font-display font-bold text-gray-900 mb-6">{branch.name}</h1>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Address Card */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Address
              </h2>
              <p className="text-gray-600">{branch.address}</p>
              <p className="text-gray-600">{branch.city}, {branch.region}</p>
              <p className="text-gray-600">{branch.country}</p>

              {/* Directions Button */}
              {(branch.latitude && branch.longitude) && (
                <a
                  href={branch.google_maps_url || `https://www.google.com/maps/dir/?api=1&destination=${branch.latitude},${branch.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  </svg>
                  {t('branches.getDirections')}
                </a>
              )}
            </div>

            {/* Amenities Card */}
            {branch.amenities && branch.amenities.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="font-semibold text-gray-900 mb-4">{t('branches.amenities.title')}</h2>
                <div className="flex flex-wrap gap-3">
                  {branch.amenities.map((amenity) => (
                    <span
                      key={amenity}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg"
                    >
                      <span>{AMENITY_ICONS[amenity] || '✓'}</span>
                      <span>{t(`branches.amenities.${amenity}`)}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Gallery */}
            {allImages.length > 1 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="font-semibold text-gray-900 mb-4">{t('branches.gallery')}</h2>
                <div className="grid grid-cols-3 gap-2">
                  {allImages.map((url, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(url)}
                      className="aspect-square rounded-lg overflow-hidden hover:opacity-90 transition-opacity"
                    >
                      <img src={url} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="font-semibold text-gray-900 mb-4">Contact</h2>

              {branch.phone && (
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('branches.phone')}</p>
                    <a href={`tel:${branch.phone}`} className="text-gray-900 font-medium hover:text-primary-600">
                      {branch.phone}
                    </a>
                  </div>
                </div>
              )}

              {branch.email && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <a href={`mailto:${branch.email}`} className="text-gray-900 font-medium hover:text-primary-600">
                      {branch.email}
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* Services Card */}
            {branch.services && branch.services.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="font-semibold text-gray-900 mb-4">{t('branches.services')}</h2>
                <div className="flex flex-wrap gap-2">
                  {branch.services.map((service) => (
                    <span
                      key={service}
                      className={`px-3 py-1 rounded-full text-sm ${SERVICE_COLORS[service] || 'bg-gray-100 text-gray-700'}`}
                    >
                      {t(`branches.${service.replace('_', '')}`)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* View Menu Button */}
            <Link
              to={`/menu/${branch.slug || branch.id}`}
              className="block w-full px-6 py-3 bg-primary-600 text-white text-center rounded-xl font-semibold hover:bg-primary-700 transition-colors"
            >
              {t('menu.title')}
            </Link>
          </div>
        </div>
      </div>

      {/* Image Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-full transition-colors"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img
            src={selectedImage}
            alt=""
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
